import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiCard, UiCardHeader, UiCardBody, UiBreadcrumb, UiPagination } from 'components/common'
import { Box, Flex, Grid, GridItem, Heading, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import { useDebouncedCallback } from 'use-debounce'
import { useParams } from 'react-router-dom'
import { getTeamTaskReportIndividual, getUserById } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { ProjectSectionTaskRow } from '../project-detail/tasks/ProjectSectionTaskRow'
import { ModalRemoveTask, ModalTask, ModalTaskType } from '../project-detail/tasks/ModalTask'
import { DrawerProjectSectionTaskNote } from '../project-detail/tasks/DrawerProjectSectionTaskNote'

export const ProjectTeamTaskReportIndividual = () => {
  const { pmId } = useParams()
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 20,
    search: '',
    orderBy: {
      key: 'dueDate',
      direction: 'asc'
    }
  })
  const [isLoadingPm, setIsLoadingPm] = useState(false)
  const [isLoadingTasks, setIsLoadingTasks] = useState(false)
  const [rows, setRows] = useState([])
  const [pm, setPm] = useState(null)

  const disclosureTask = useDisclosure()
  const disclosureRemoveTask = useDisclosure()
  const [selectedTask, setSelectedTask] = useState(null)

  const disclosureTaskNotes = useDisclosure()
  const [selectedTaskIdForNotes, setSelectedTaskIdForNotes] = useState(0)

  const [keyForNotesReload, setKeyForNotesReload] = useState(0)

  useEffect(() => {
    const loadPm = async () => {
      setIsLoadingPm(true)
      try {
        const response = await getUserById(pmId)
        setPm(response)
      } catch (err) {
        createToast('error', 'Error', 'Failed to load project manager.')
      }
      setIsLoadingPm(false)
    }
    loadPm()
  }, [])

  const load = async () => {
    setIsLoadingTasks(true)
    try {
      const response = await getTeamTaskReportIndividual(
        pmId,
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
    } catch (err) {
      console.log(err)
    }
    setIsLoadingTasks(false)
  }

  const debouncedLoad = useDebouncedCallback(load, 400)

  useEffect(() => {
    debouncedLoad()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const BreadcrumbNav = useMemo(() => {
    return [
      {
        label: 'Team Task Report',
        path: '/projects/team-task-report'
      },
      {
        label: isLoadingPm ? 'Loading ...' : `${pm?.firstname} ${pm?.lastname}`,
        isCurrentPage: true
      }
    ]
  }, [pm])

  const onEditTask = (task) => {
    disclosureTask.onOpen()
    setSelectedTask({ ...task })
  }

  const onRemoveTask = (task) => {
    disclosureRemoveTask.onOpen()
    setSelectedTask({ ...task })
  }

  const onOpenTaskNotes = (taskId) => {
    setSelectedTaskIdForNotes(taskId)
    disclosureTaskNotes.onOpen()
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box my={8}>
        {pm !== null ? (
          <UiCard borderRadius={0}>
            <UiCardHeader bg="gray.500" p={4}>
              <Heading color="white" size="sm">{pm.firstname} {pm.lastname} Tasks</Heading>
            </UiCardHeader>
            <UiCardBody p={8}>
              {isLoadingTasks ? (
                <Flex alignItems="center" gap={4}>
                  <Spinner color="blue.400" size="sm" />
                  <Text fontSize="sm">Loading ...</Text>
                </Flex>
              ) : (
                rows.length > 0 ? (
                  <>
                    <Grid
                      templateColumns="repeat(15, 1fr)"
                      fontWeight="bold"
                      fontSize="sm"
                      borderBottomWidth={1}
                      borderBottomStyle="solid"
                      borderBottomColor="gray.200"
                      pl={8}
                      pr={4}
                      pb={2}
                      gap={4}
                    >
                      <GridItem colSpan={6}>Task name</GridItem>
                      <GridItem colSpan={2}>Assignee</GridItem>
                      <GridItem colSpan={2}>Due Date</GridItem>
                      <GridItem colSpan={4}>Project</GridItem>
                      <GridItem colSpan={1}></GridItem>
                    </Grid>
                    {rows.map(row => {
                      return (
                        <ProjectSectionTaskRow
                          key={row.projectId}
                          task={row}
                          onEditTask={onEditTask}
                          onRemoveTask={onRemoveTask}
                          reload={load}
                          onOpenTaskNotes={onOpenTaskNotes}
                          showProjectName={true}
                        />
                      )
                    })}
                  </>
                ) : (
                  <Text>No tasks available</Text>
                )
              )}
              <UiPagination
                pt={8}
                curPage={tableConfig.curPage}
                totalPages={tableConfig.totalPages}
                onSelect={(v) => setTableConfig(draft => { draft.curPage = v })}
              />
            </UiCardBody>
          </UiCard>
        ) : (
          <Text>{isLoadingPm ? 'Loading ...' : 'Project manager does not exist.'}</Text>
        )}
      </Box>
      {disclosureTask.isOpen && (
        <ModalTask
          modalType={!selectedTask?.id ? ModalTaskType.Add : ModalTaskType.Edit}
          projectId={selectedTask?.projectId}
          task={selectedTask}
          reload={() => {
            setSelectedTask(null)
            load()
            if (selectedTaskIdForNotes === selectedTask?.id) {
              setKeyForNotesReload(keyForNotesReload + 1)
            }
          }}
          {...disclosureTask}
        />
      )}
      {disclosureRemoveTask.isOpen && (
        <ModalRemoveTask
          task={selectedTask}
          reload={() => {
            setSelectedTask(null)
            load()
          }}
          {...disclosureRemoveTask}
        />
      )}
      {disclosureTaskNotes.isOpen && (
        <DrawerProjectSectionTaskNote
          keyForReload={keyForNotesReload}
          taskId={selectedTaskIdForNotes}
          onEditTask={onEditTask}
          {...disclosureTaskNotes}
        />
      )}
    </>
  )
}

ProjectTeamTaskReportIndividual.propTypes = {
  data: PropTypes.object
}
